import { Component } from '@theme/component';

/** Bundle enhancement keeps the native product form as a no-JS/express fallback. */
class HandonsieBundle extends Component {
  controller;
  busy = false;
  restore = [];
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    super.updatedCallback();
    this.controller?.abort();
    this.busy = false;
    this.initialize();
  }
  disconnectedCallback() {
    this.controller?.abort();
    for (const [element, wasHidden] of this.restore) element.hidden = wasHidden;
    this.restore = [];
    super.disconnectedCallback();
  }
  initialize() {
    try {
      this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.scope = this.closest('.shopify-section') || this.parentElement.parentElement;
      this.picker = this.scope.querySelector('variant-picker[data-product-id="' + this.dataset.productId + '"]');
      this.nativeForm = this.parentElement.querySelector('product-form-component');
      if (!this.restore.length) {
        for (const element of [this.picker, this.nativeForm]) {
          if (element) this.restore.push([element, element.hidden]);
        }
      }
      if (this.picker) this.picker.hidden = true;
      if (this.nativeForm) this.nativeForm.hidden = true;
      this.hidden = false;
      this.refresh();
    } catch (error) {
      this.hidden = true;
      for (const [element, wasHidden] of this.restore) element.hidden = wasHidden;
      console.warn('Bundle selector unavailable; using the native product form.', error);
    }
  }
  money(cents) { return this.formatter.format(cents / 100); }
  selectedCard() { return this.querySelector('input[type="radio"]:checked')?.closest('[data-count]'); }
  /** @param {Event} event */
  handleChange(event) {
    this.querySelector('[data-status]').textContent = '';
    this.refresh();
  }
  /** @param {Event} event */
  handleExpress(event) {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (this.nativeForm) this.nativeForm.hidden = !event.target.open;
    if (this.picker) this.picker.hidden = !event.target.open;
  }
  collect(card) {
    const items = new Map();
    let price = 0;
    let compare = 0;
    let error = '';
    const rate = Number(card.dataset.rate) / 100;
    const add = (id) => items.set(id, (items.get(id) || 0) + 1);
    for (const row of card.querySelectorAll('[data-item-row]')) {
      const colour = row.querySelector('[data-colour]').value;
      const size = row.querySelector('[data-size]').value;
      const packageSelect = row.querySelector('[data-package]');
      const shoeOption = packageSelect.querySelector('option[value="shoes"]');
      const isKids = size.toLowerCase().startsWith('kids');
      shoeOption.disabled = isKids;
      if (isKids && packageSelect.value === 'shoes') packageSelect.value = 'only';
      const variant = this.data.variants.find(v => v.colour === colour && v.size === size);
      for (const option of row.querySelector('[data-size]').options) {
        const candidate = this.data.variants.find(v => v.colour === colour && v.size === option.value);
        option.disabled = !candidate?.available;
      }
      if (!variant?.available) error = this.data.labels.unavailable;
      if (variant) {
        add(variant.id);
        price += variant.price * rate;
        compare += Math.max(variant.compare || 0, variant.price);
      }
      if (packageSelect.value === 'shoes') {
        const shoe = this.data.shoes.find(v => v.colour === colour);
        if (!shoe?.available) error = this.data.labels.shoeUnavailable;
        if (shoe) { add(shoe.id); price += shoe.price; compare += shoe.price; }
      }
    }
    return { price: Math.round(price), compare, items: Array.from(items, ([id, quantity]) => ({ id, quantity })), error };
  }
  refresh() {
    const selected = this.selectedCard();
    if (!selected) return;
    for (const card of this.querySelectorAll('[data-count]')) {
      card.querySelector('.hds-bundle__items').hidden = card !== selected;
      const result = this.collect(card);
      card.querySelector('[data-card-total]').textContent = this.money(result.price);
      card.querySelector('[data-card-compare]').textContent = this.money(result.compare);
      card.querySelector('[data-card-each]').textContent = this.money(Math.round(result.price / Number(card.dataset.count)));
    }
    const result = this.collect(selected);
    this.querySelector('[data-total]').textContent = this.money(result.price);
    this.querySelector('[data-compare]').textContent = this.money(result.compare);
    this.querySelector('[data-welcome-total]').textContent = this.money(Math.round(result.price * .95));
    const button = this.querySelector('[data-add]');
    button.disabled = this.busy || !!result.error;
    if (result.error) this.querySelector('[data-status]').textContent = result.error;
  }
  /** Add all clothing and matching slippers as separate real variants. @param {Event} event */
  async handleAdd(event) {
    event.preventDefault();
    if (this.busy) return;
    const card = this.selectedCard();
    if (!card) return;
    const result = this.collect(card);
    const status = this.querySelector('[data-status]');
    if (result.error) { status.textContent = result.error; return; }
    this.busy = true;
    const button = this.querySelector('[data-add]');
    button.disabled = true;
    button.textContent = this.data.labels.adding;
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: result.items }), signal: this.controller.signal
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || this.data.labels.error);
      const destination = new URL(this.dataset.cartUrl, location.origin);
      location.assign(destination.href);
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = error.message || this.data.labels.error;
      this.busy = false;
      button.textContent = this.data.labels.add;
      this.refresh();
    }
  }
}
if (!customElements.get('handonsie-bundle-component')) customElements.define('handonsie-bundle-component', HandonsieBundle);
